import { t } from "@/lib/i18n/interpolate";
import type { DocSection, DocumentModel } from "@/lib/tool-kit/document";
import { paragraphsOrPrompt } from "@/lib/tool-kit/essay-templates/text";

/**
 * A STAR answer: Situation, Task, Action, Result, with an optional fifth step for what
 * the writer learned. The module does the arithmetic the widget shows — how many words
 * each part should have for a target time, how long the answer takes to say, who the
 * Action's verbs belong to — and assembles the answer as a document. It never writes
 * any of the answer: the words are the candidate's.
 */

export const STAR_PARTS = [
  "situation",
  "task",
  "action",
  "result",
  "learning",
] as const;

export type StarPart = (typeof STAR_PARTS)[number];

/**
 * Planning shares of the words. The four STAR parts follow the University of Washington
 * Career & Internship Services guideline (Situation 20%, Task 10%, Action 60%, Result
 * 10%), one of several published splits that all put most of the answer in the Action.
 * With the optional Learning step, it takes 10% from the Action.
 */
export const STAR_SHARES: Record<StarPart, number> = {
  situation: 0.2,
  task: 0.1,
  action: 0.6,
  result: 0.1,
  learning: 0,
};

export const STAR_SHARES_WITH_LEARNING: Record<StarPart, number> = {
  situation: 0.2,
  task: 0.1,
  action: 0.5,
  result: 0.1,
  learning: 0.1,
};

export const DEFAULT_PACE = 140;
export const DEFAULT_TARGET_SECONDS = 120;
export const MIN_PACE = 80;
export const MAX_PACE = 220;

const CORE_PARTS: readonly StarPart[] = [
  "situation",
  "task",
  "action",
  "result",
];

export function activeParts(withLearning: boolean): readonly StarPart[] {
  return withLearning ? STAR_PARTS : CORE_PARTS;
}

export function sharesFor(withLearning: boolean): Record<StarPart, number> {
  return withLearning ? STAR_SHARES_WITH_LEARNING : STAR_SHARES;
}

/** Seconds to say `words` words at `pace` words per minute. */
export function speakingSeconds(words: number, pace: number): number {
  if (pace <= 0) {
    return 0;
  }
  return (words / pace) * 60;
}

/** The words that fit in `seconds` at `pace` words per minute, rounded to the nearest word. */
export function wordsForSeconds(seconds: number, pace: number): number {
  return Math.round((seconds * pace) / 60);
}

/** The word budget for each part of an answer of `targetSeconds`. */
export function wordBudget(
  targetSeconds: number,
  pace: number,
  withLearning: boolean
): Record<StarPart, number> {
  const total = wordsForSeconds(targetSeconds, pace);
  const shares = sharesFor(withLearning);
  const budget = {} as Record<StarPart, number>;
  for (const part of STAR_PARTS) {
    budget[part] = Math.round(total * shares[part]);
  }
  return budget;
}

/** 95 → "1:35"; 120 → "2:00". Whole seconds, rounded. */
export function clock(seconds: number): string {
  const whole = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(whole / 60);
  const rest = whole % 60;
  return `${minutes}:${rest.toString().padStart(2, "0")}`;
}

const SINGULAR = new Set([
  "i",
  "i'm",
  "i've",
  "i'd",
  "i'll",
  "me",
  "my",
  "mine",
  "myself",
]);
const PLURAL = new Set([
  "we",
  "we're",
  "we've",
  "we'd",
  "we'll",
  "us",
  "our",
  "ours",
  "ourselves",
]);
const WORD = /[\p{L}\p{N}]+(?:['’][\p{L}]+)?/gu;
const CURLY_APOSTROPHE = /’/g;

export interface PronounCount {
  /** I, me, my, mine, myself and their contractions. */
  singular: number;
  /** We, us, our, ours, ourselves and their contractions. */
  plural: number;
}

/**
 * First-person words in a passage, for the Action check: interviewers listen for what
 * you yourself did, so "we" throughout the Action is the most common thing to fix. English-only
 * by nature — the check is skipped for text it cannot read as English.
 */
export function pronounCount(text: string): PronounCount {
  let singular = 0;
  let plural = 0;
  for (const match of text.matchAll(WORD)) {
    const word = match[0].toLowerCase().replace(CURLY_APOSTROPHE, "'");
    if (SINGULAR.has(word)) {
      singular += 1;
    } else if (PLURAL.has(word)) {
      plural += 1;
    }
  }
  return { singular, plural };
}

const DIGIT = /\d/;

/** True when the Result states something countable — a number, a percentage, a rank. */
export function hasMeasure(text: string): boolean {
  return DIGIT.test(text);
}

export interface StarInput {
  question: string;
  labelled: boolean;
  withLearning: boolean;
  parts: Record<StarPart, string>;
}

export interface StarLabels {
  defaultTitle: string;
  titleWithQuestion: string;
  subtitle: string;
  voiceNote: string;
  partNames: Record<StarPart, string>;
  partPrompts: Record<StarPart, string>;
  partPromptNamed: string;
  timingNote: string;
}

export interface StarTiming {
  words: number;
  pace: number;
  targetSeconds: number;
}

export function buildStarAnswer(
  input: StarInput,
  labels: StarLabels,
  timing: StarTiming,
  clockText: (seconds: number) => string = clock
): DocumentModel {
  const question = input.question.trim();
  const sections: DocSection[] = activeParts(input.withLearning).map((part) => {
    const prompt = input.labelled
      ? labels.partPrompts[part]
      : t(labels.partPromptNamed, {
          part: labels.partNames[part],
          prompt: labels.partPrompts[part],
        });
    return {
      heading: input.labelled ? labels.partNames[part] : undefined,
      blocks: paragraphsOrPrompt(input.parts[part], prompt),
    };
  });
  return {
    title: question
      ? t(labels.titleWithQuestion, { question })
      : labels.defaultTitle,
    subtitle: labels.subtitle,
    sections: [
      { blocks: [{ kind: "guidance", text: labels.voiceNote }] },
      ...sections,
      {
        blocks: [
          {
            kind: "guidance",
            text: t(labels.timingNote, {
              time: clockText(speakingSeconds(timing.words, timing.pace)),
              pace: timing.pace,
              target: clockText(timing.targetSeconds),
            }),
          },
        ],
      },
    ],
  };
}

/* ------------------------------------------------------------------ the answer's checks */

export type StarCheckKey =
  | "lengthOk"
  | "lengthOver"
  | "lengthUnder"
  | "actionOk"
  | "actionLow"
  | "actionHigh"
  | "voiceOk"
  | "voiceWe"
  | "measureOk"
  | "measureMissing";

export interface StarCheck {
  key: StarCheckKey;
  /** True when nothing needs attention. */
  ok: boolean;
  values: Record<string, number>;
}

export interface PartTiming {
  part: StarPart;
  words: number;
  seconds: number;
  budget: number;
}

export interface StarAnalysis {
  totalWords: number;
  totalSeconds: number;
  parts: PartTiming[];
  checks: StarCheck[];
}

/** An answer within this fraction of the target time counts as on target. */
export const LENGTH_TOLERANCE = 0.1;
/** The Action should hold roughly its planned share: 45–75% of the words with the 60% plan. */
export const ACTION_SHARE_RANGE = { low: 0.45, high: 0.75 } as const;

const WHITESPACE = /\s+/;

/** Words as people count them for speaking time: runs of non-space characters. */
export function spokenWords(text: string): number {
  const trimmed = text.trim();
  return trimmed === "" ? 0 : trimmed.split(WHITESPACE).length;
}

function lengthCheck(
  totalWords: number,
  totalSeconds: number,
  targetSeconds: number
): StarCheck | null {
  if (totalWords === 0) {
    return null;
  }
  const difference = Math.round(Math.abs(totalSeconds - targetSeconds));
  const values = { seconds: difference };
  if (
    Math.abs(totalSeconds - targetSeconds) <=
    targetSeconds * LENGTH_TOLERANCE
  ) {
    return { key: "lengthOk", ok: true, values };
  }
  return totalSeconds > targetSeconds
    ? { key: "lengthOver", ok: false, values }
    : { key: "lengthUnder", ok: false, values };
}

function actionChecks(
  actionText: string,
  actionWords: number,
  totalWords: number
): StarCheck[] {
  if (actionWords === 0) {
    return [];
  }
  const share = actionWords / totalWords;
  const percent = { percent: Math.round(share * 100) };
  const { singular, plural } = pronounCount(actionText);
  let shareKey: StarCheckKey = "actionOk";
  if (share < ACTION_SHARE_RANGE.low) {
    shareKey = "actionLow";
  } else if (share > ACTION_SHARE_RANGE.high) {
    shareKey = "actionHigh";
  }
  const shareCheck: StarCheck = {
    key: shareKey,
    ok: shareKey === "actionOk",
    values: percent,
  };
  const voiceCheck: StarCheck = {
    key: singular >= plural ? "voiceOk" : "voiceWe",
    ok: singular >= plural,
    values: { singular, plural },
  };
  return [shareCheck, voiceCheck];
}

export function analyzeStar(
  parts: Record<StarPart, string>,
  withLearning: boolean,
  targetSeconds: number,
  pace: number
): StarAnalysis {
  const budget = wordBudget(targetSeconds, pace, withLearning);
  const timings = activeParts(withLearning).map((part): PartTiming => {
    const words = spokenWords(parts[part]);
    return {
      part,
      words,
      seconds: speakingSeconds(words, pace),
      budget: budget[part],
    };
  });
  const totalWords = timings.reduce((sum, item) => sum + item.words, 0);
  const totalSeconds = speakingSeconds(totalWords, pace);
  const checks: StarCheck[] = [];
  const length = lengthCheck(totalWords, totalSeconds, targetSeconds);
  if (length) {
    checks.push(length);
  }
  const actionWords = spokenWords(parts.action);
  checks.push(...actionChecks(parts.action, actionWords, totalWords));
  if (spokenWords(parts.result) > 0) {
    checks.push(
      hasMeasure(parts.result)
        ? { key: "measureOk", ok: true, values: {} }
        : { key: "measureMissing", ok: false, values: {} }
    );
  }
  return { totalWords, totalSeconds, parts: timings, checks };
}
